<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Livraison
            <span v-if="delivery" class="text-muted">#{{ delivery.id }}</span>
          </h1>
          <div v-if="delivery" class="small text-muted">
            Créée le {{ $format.datetime(delivery.created_at) }}
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'DeliveriesView' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!delivery || delivery.status === 'delivered'"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-success btn-sm btn-icon-label"
            :disabled="!delivery || delivery.status === 'delivered' || delivery.status === 'cancelled'"
            title="Marquer livrée"
            @click="updateStatus('delivered')"
          >
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Marquer livrée</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-warning btn-sm btn-icon-label"
            :disabled="!delivery || delivery.status === 'delivered' || delivery.status === 'cancelled'"
            title="Annuler"
            @click="updateStatus('cancelled')"
          >
            <i class="fa fa-ban"></i>
            <span class="btn-label ms-1">Annuler</span>
          </button>

          <AppLoadingButton
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            :loading="isDeleting"
            :disabled="!delivery || delivery.status === 'delivered'"
            loading-label="Suppression..."
            title="Supprimer"
            @click="confirmDelete"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement de la livraison...</div>
      </div>

      <div v-else-if="!delivery && !errorMessage" class="alert alert-warning">
        Livraison introuvable.
      </div>

      <div v-else-if="delivery" class="row g-3 align-items-start">
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Informations livraison</h6>
              <span class="badge" :class="statusClass(delivery.status)">
                {{ statusLabel(delivery.status) }}
              </span>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-2">
                <div class="small text-muted">Zone</div>
                <div class="fw-semibold">{{ delivery.zone?.name || delivery.zone_name_snapshot || '-' }}</div>
              </div>
              <div class="mb-2">
                <div class="small text-muted">Frais</div>
                <div>{{ $format.currency(delivery.delivery_fee || 0) }}</div>
              </div>
              <div class="mb-2">
                <div class="small text-muted">Livreur</div>
                <div>{{ delivery.deliverer?.name || '-' }}</div>
              </div>
              <div v-if="delivery.deliverer?.phone" class="mb-2">
                <div class="small text-muted">Téléphone livreur</div>
                <div>{{ delivery.deliverer.phone }}</div>
              </div>
              <div class="mb-2">
                <div class="small text-muted">Créée par</div>
                <div>{{ delivery.creator?.name || '-' }}</div>
              </div>
              <div v-if="delivery.delivered_at" class="mb-2">
                <div class="small text-muted">Livrée le</div>
                <div>{{ $format.datetime(delivery.delivered_at) }}</div>
              </div>
              <div v-if="delivery.notes" class="mt-2">
                <div class="small text-muted">Notes</div>
                <div>{{ delivery.notes }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Vente associée</h6>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="small text-muted">Référence</div>
                  <button
                    v-if="delivery.sale"
                    type="button"
                    class="btn btn-link p-0 fw-semibold"
                    @click="$router.push({ name: 'SaleShow', params: { id: delivery.sale.id } })"
                  >
                    <i class="fa fa-eye me-1" aria-hidden="true"></i>
                    {{ delivery.sale.reference || `Vente #${delivery.sale.id}` }}
                  </button>
                  <div v-else>-</div>
                </div>
                <div class="col-md-6">
                  <div class="small text-muted">Client</div>
                  <div>{{ delivery.sale?.customer_name || 'comptant' }}</div>
                </div>
                <div class="col-md-6">
                  <div class="small text-muted">Montant vente</div>
                  <div class="fw-semibold">{{ $format.currency(delivery.sale?.total_amount || 0) }}</div>
                </div>
                <div class="col-md-6">
                  <div class="small text-muted">Statut vente</div>
                  <div>{{ delivery.sale?.status || '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: '900px' }"
      >
        <template #header>
          <h5 class="mb-0">Modifier la livraison</h5>
        </template>

        <DeliveryForm
          v-if="delivery"
          mode="edit"
          :delivery="delivery"
          :zones="zones"
          :deliverers="deliverers"
          @saved="onSaved"
          @cancel="closeDialog"
        />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import DeliveryForm from './DeliveryForm.vue';

export default {
  name: 'DeliveryShowView',
  components: { DeliveryForm },
  data() {
    return {
      delivery: null,
      zones: [],
      deliverers: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      dialogVisible: false,
      isDeleting: false,
    };
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      await Promise.all([this.fetchZones(), this.fetchDeliverers()]);
      await this.fetchDelivery();
    },

    async fetchDelivery() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get(`/deliveries/${this.$route.params.id}`);
        this.delivery = res.data.data || res.data;
      } catch (error) {
        console.error('fetchDelivery', error);
        this.delivery = null;
        this.errorMessage = 'Impossible de charger cette livraison.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchZones() {
      try {
        const res = await this.$api.get('/delivery-zones');
        this.zones = res.data.data || res.data || [];
      } catch (error) {
        console.error('fetchZones', error);
      }
    },

    async fetchDeliverers() {
      try {
        const res = await this.$api.get('/deliverers', { params: { only_active: true } });
        this.deliverers = res.data.data || res.data || [];
      } catch (error) {
        console.error('fetchDeliverers', error);
      }
    },

    statusLabel(status) {
      if (status === 'pending') return 'En attente';
      if (status === 'assigned') return 'Assignée';
      if (status === 'delivered') return 'Livrée';
      if (status === 'cancelled') return 'Annulée';
      return status || '-';
    },

    statusClass(status) {
      if (status === 'pending') return 'bg-secondary';
      if (status === 'assigned') return 'bg-info';
      if (status === 'delivered') return 'bg-success';
      if (status === 'cancelled') return 'bg-danger';
      return 'bg-secondary';
    },

    openEditDialog() {
      if (!this.delivery) return;
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    async onSaved(message) {
      this.dialogVisible = false;
      this.successMessage = message || 'Livraison mise à jour.';
      await this.fetchDelivery();
    },

    async updateStatus(status) {
      if (!this.delivery) return;

      const label = status === 'delivered' ? 'marquer cette livraison comme livrée' : 'annuler cette livraison';
      const ok = await this.$confirmAction(`Voulez-vous ${label} ?`);
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.put(`/deliveries/${this.delivery.id}`, { status });
        this.successMessage = status === 'delivered' ? 'Livraison marquée comme livrée.' : 'Livraison annulée.';
        await this.fetchDelivery();
      } catch (error) {
        console.error('updateStatus delivery', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Erreur lors de la mise à jour.';
      }
    },

    async confirmDelete() {
      if (!this.delivery) return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction('Supprimer cette livraison ?');
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/deliveries/${this.delivery.id}`);
        this.$router.push({ name: 'DeliveriesView' });
      } catch (error) {
        console.error('delete delivery', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Erreur suppression.';
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}
</style>
